import { useState } from 'react';

export interface TextareaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
  error?: boolean | string;
  fullWidth?: boolean;
  resize?: 'none' | 'vertical' | 'both';
  label?: string;
  helperText?: string;
  maxLength?: number;
  onBlur?: () => void;
  autoFocus?: boolean;
  variant?: 'default' | 'naked';
}

const LABEL_STYLE = {
  display: 'block' as const,
  marginBottom: '4px',
  fontSize: '11px',
  fontWeight: 500,
  color: 'var(--evo-fg-3)',
  fontFamily: 'var(--evo-font)',
} as const;

const HELPER_STYLE = {
  display: 'block' as const,
  marginTop: '4px',
  fontSize: '11px',
  fontFamily: 'var(--evo-font)',
} as const;

export function Textarea({
  value,
  onChange,
  placeholder,
  rows = 3,
  disabled = false,
  error,
  fullWidth = false,
  resize = 'vertical',
  label,
  helperText,
  maxLength,
  onBlur,
  autoFocus = false,
  variant = 'default',
}: TextareaProps) {
  const [focused, setFocused] = useState(false);

  if (variant === 'naked') {
    return (
      <textarea
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        maxLength={maxLength}
        autoFocus={autoFocus}
        onChange={e => onChange(e.target.value)}
        onBlur={onBlur}
        style={{
          display: 'block',
          width: fullWidth ? '100%' : 'auto',
          background: 'transparent',
          border: 'none',
          outline: 'none',
          color: 'var(--evo-fg-2)',
          fontSize: '13px',
          fontFamily: 'var(--evo-font)',
          padding: 0,
          resize,
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'not-allowed' : 'text',
          boxSizing: 'border-box',
          lineHeight: 1.5,
        }}
      />
    );
  }

  const hasError = !!error;
  const errorMsg = typeof error === 'string' ? error : undefined;
  const borderColor = hasError ? 'var(--evo-danger)' : focused ? 'var(--evo-cyan)' : 'var(--evo-stroke)';
  const shadow      = focused ? `0 0 0 1px ${hasError ? 'var(--evo-danger)' : 'var(--evo-cyan)'}` : 'none';

  return (
    <div style={{ display: fullWidth ? 'block' : 'inline-block', width: fullWidth ? '100%' : 'auto' }}>
      {label != null && <label style={LABEL_STYLE}>{label}</label>}
      <textarea
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        maxLength={maxLength}
        onChange={e => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => { setFocused(false); onBlur?.(); }}
        autoFocus={autoFocus}
        style={{
          display: 'block',
          width: fullWidth ? '100%' : 'auto',
          background: 'var(--evo-bg-3)',
          border: `1px solid ${borderColor}`,
          borderRadius: 'var(--evo-r)',
          color: 'var(--evo-fg-2)',
          fontSize: '13px',
          fontFamily: 'var(--evo-font)',
          padding: '8px 10px',
          outline: 'none',
          resize,
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'not-allowed' : 'text',
          transition: 'border-color 120ms linear, box-shadow 120ms linear',
          boxShadow: shadow,
          boxSizing: 'border-box',
          lineHeight: 1.5,
        }}
      />
      {(helperText != null || errorMsg != null) && (
        <span style={{ ...HELPER_STYLE, color: errorMsg != null ? 'var(--evo-danger)' : 'var(--evo-fg-3)' }}>
          {errorMsg ?? helperText}
        </span>
      )}
    </div>
  );
}
